import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Section } from '@/components/Section';
import { getSignatureView, type SignatureView } from '@/lib/api';

/**
 * Read-only viewer for a signed waiver. Requires a server-issued HMAC token
 * (`?t=...` in the URL) bound to the signature id — without it, the API
 * returns 404 even if the cuid is correct.
 */
export function WaiverViewerPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [data, setData] = useState<SignatureView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    if (!token) {
      setError('This link is missing its access token. Use the link from your confirmation email.');
      return;
    }
    getSignatureView(id, token)
      .then(setData)
      .catch((e: unknown) =>
        setError(`Couldn't load signature: ${String((e as Error).message ?? e)}`)
      );
  }, [id, token]);

  if (error) {
    return (
      <Section eyebrow="Waiver" title="Signature">
        <p className="text-sm text-accent">{error}</p>
      </Section>
    );
  }
  if (!data) {
    return (
      <Section eyebrow="Waiver" title="Signature">
        <p className="text-sm text-bone/60">Loading…</p>
      </Section>
    );
  }
  return (
    <Section eyebrow="Waiver" title={data.waiver.title}>
      <div className="grid gap-6 max-w-3xl">
        <dl className="grid grid-cols-[12rem_1fr] gap-y-2 text-sm" data-testid="signature-meta">
          <dt className="text-bone/50">Signed by</dt>
          <dd className="text-bone">{data.typedSignature}</dd>
          <dt className="text-bone/50">Signed at</dt>
          <dd className="text-bone">{data.signedAt}</dd>
          <dt className="text-bone/50">IP</dt>
          <dd className="font-mono text-bone/80">{data.ip}</dd>
          <dt className="text-bone/50">User agent</dt>
          <dd className="font-mono text-xs text-bone/80">{data.userAgent}</dd>
          <dt className="text-bone/50">Content hash</dt>
          <dd className="break-all font-mono text-xs text-bone/80">{data.contentHash}</dd>
          <dt className="text-bone/50">Intent</dt>
          <dd className="text-bone/80">{data.intent}</dd>
          <dt className="text-bone/50">Version</dt>
          <dd className="text-bone/80">v{data.waiver.version}</dd>
        </dl>
        <div>
          <h3 className="mb-2 text-xs uppercase tracking-wider text-bone/50">
            Waiver text at time of signing
          </h3>
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded border border-bone/10 bg-neutral-900/50 p-4 font-mono text-xs leading-relaxed text-bone/80">
            {data.contentSnapshot}
          </pre>
        </div>
      </div>
    </Section>
  );
}
